import { useAtom } from 'jotai'
import { useCallback } from 'react'
import { useDeleteBalancesCache } from '@/src/hooks/balances/use-delete-balances-cache'
import { useAmplitudeAnalytics } from '@/src/hooks/use-amplitude-analytics'
import {
  type PortfolioDrawerView,
  portfolioDrawerHideBalancesAtom,
  portfolioDrawerOpenAtom,
  portfolioDrawerSelectedViewAtom,
} from './atoms'

export const usePortfolioDrawer = () => {
  const [isOpen, setPortfolioDrawerOpen] = useAtom(portfolioDrawerOpenAtom)
  const { currentView } = usePortfolioView()
  const [shouldHideBalances, setShouldHideBalances] = useAtom(
    portfolioDrawerHideBalancesAtom,
  )
  const { mutate } = useDeleteBalancesCache()
  const { ampli } = useAmplitudeAnalytics()

  const open = useCallback(() => {
    setPortfolioDrawerOpen(true)
    setShouldHideBalances(false)
    mutate()

    ampli?.portfolioPortfolioOpened()
  }, [setPortfolioDrawerOpen, ampli, mutate, setShouldHideBalances])

  const close = useCallback(() => {
    setPortfolioDrawerOpen(false)

    ampli?.portfolioPortfolioClosed({
      toggle: currentView.view,
    })
  }, [setPortfolioDrawerOpen, currentView, ampli])

  return {
    isOpen,
    open,
    close,
    shouldHideBalances,
    setShouldHideBalances,
  }
}

export const usePortfolioView = () => {
  const [selectedView, setSelectedView] = useAtom(
    portfolioDrawerSelectedViewAtom,
  )
  const { ampli } = useAmplitudeAnalytics()

  const updateView = useCallback(
    ({ view, metadata }: { view: PortfolioDrawerView; metadata?: unknown }) => {
      if (view === 'wallets') ampli?.portfolioManageWallets()
      if (view === 'account') ampli?.portfolioAccountViewed()
      if (view === 'manage-account') ampli?.portfolioManageAccount()
      if (view === 'manage-wallet')
        ampli?.portfolioManageWallet({
          wallet: (metadata as { walletName: string }).walletName,
        })
      if (view === 'settings') ampli?.portfolioSettings()
      if (view === 'referrals') ampli?.portfolioReferrals()

      setSelectedView({
        view,
        metadata,
        stack: [...selectedView.stack, selectedView.view],
      })
    },
    [setSelectedView, selectedView.stack, selectedView.view, ampli],
  )

  const updateMetadata = useCallback(
    ({ metadata }: { metadata?: unknown }) => {
      setSelectedView({
        view: selectedView.view,
        metadata,
        stack: selectedView.stack,
      })
    },
    [setSelectedView, selectedView.stack, selectedView.view],
  )

  const goBack = useCallback(() => {
    setSelectedView({
      view: selectedView.stack[selectedView.stack.length - 1],
      metadata: selectedView.metadata,
      stack: selectedView.stack.slice(0, selectedView.stack.length - 1),
    })
  }, [setSelectedView, selectedView.metadata, selectedView.stack])

  const reset = useCallback(() => {
    setSelectedView(portfolioDrawerSelectedViewAtom.init)
  }, [setSelectedView])

  return {
    currentView: {
      view: selectedView.view,
      metadata: selectedView.metadata,
    },
    updateMetadata,
    updateView,
    goBack,
    reset,
  }
}
